import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-brand">
        <img src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-8 w-8 text-brand-accent" />
            <span className="font-display font-black text-2xl tracking-tight">CMMC Shield</span>
          </div>
          <div>
            <h2 className="font-display font-bold text-3xl leading-tight mb-3">Get your real SPRS score this week.</h2>
            <p className="text-slate-300 max-w-md">Most contractors your size are sitting at a negative SPRS score and don't know it. Find out in minutes.</p>
          </div>
          <div className="text-xs text-slate-500 font-mono">SPRS range: -203 to +110</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <h1 className="font-display font-bold text-2xl tracking-tight mb-1">Create your account</h1>
          <p className="text-slate-500 text-sm mb-6">Spin up your org workspace in seconds.</p>
          {error && <div className="mb-4 text-sm text-danger bg-danger-bg border border-red-200 rounded px-3 py-2" data-testid="register-error">{error}</div>}
          {[["Full name", "name", "text"], ["Company name", "company_name", "text"], ["Work email", "email", "email"], ["Password (min 8)", "password", "password"]].map(([label, key, type]) => (
            <div key={key} className="mb-4">
              <label className="text-sm font-medium text-slate-700">{label}</label>
              <input value={form[key]} onChange={upd(key)} type={type} required data-testid={`register-${key}`}
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-accent" />
            </div>
          ))}
          <button disabled={loading} data-testid="register-submit"
            className="w-full bg-brand text-white rounded-md py-2.5 font-medium hover:bg-brand-secondary transition-colors flex items-center justify-center gap-2 disabled:opacity-60 mt-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
          </button>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Have an account? <Link to="/login" className="text-brand-accent font-medium" data-testid="to-login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
